import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, MapPin, Check, MessageCircle, ArrowRight, Tag, CalendarClock } from "lucide-react";
import BranchPickerModal from "@/components/site/BranchPickerModal";
import CostsModal from "@/components/site/CostsModal";

const defaultIncludes = [
  "Certificación oficial con valor curricular",
  "Prácticas reales con modelos en sucursal",
  "Profesores expertos en activo",
  "Material y kit profesional incluido",
  "Acceso a seminarios de emprendimiento sin costo",
  "Plan de pagos y becas disponibles",
];

export default function CourseModal({ course, onClose, onEnroll }) {
  const [pickerMode, setPickerMode] = useState(null); // null | 'enroll' | 'whatsapp'
  const [costsOpen, setCostsOpen] = useState(false);

  const priceValues = useMemo(
    () => (course?.prices ? Object.values(course.prices) : []),
    [course]
  );
  const numericPrices = priceValues.filter((v) => typeof v === "number");
  const minPrice = numericPrices.length ? Math.min(...numericPrices) : null;
  const hasAnyPrice = priceValues.length > 0;

  useEffect(() => {
    if (!course) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !pickerMode && !costsOpen) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [course, onClose, pickerMode, costsOpen]);

  const handleBranchSelected = (branch) => {
    if (pickerMode === "whatsapp") {
      const msg = encodeURIComponent(
        `Hola ${branch.name}, quiero información sobre el curso de ${course.title}.`
      );
      window.open(`https://wa.me/${branch.whatsapp}?text=${msg}`, "_blank", "noopener");
    } else if (pickerMode === "enroll") {
      onEnroll(course, branch);
    }
    setPickerMode(null);
    onClose();
  };

  return (
    <>
      <AnimatePresence>
        {course && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-end md:items-center justify-center"
            data-testid="course-modal-overlay"
          >
            <button
              aria-label="Cerrar"
              data-testid="course-modal-backdrop"
              onClick={onClose}
              className="absolute inset-0 bg-black/55 backdrop-blur-sm cursor-default"
            />

            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="course-modal-title"
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-white w-full md:w-[92%] md:max-w-5xl max-h-[92dvh] md:max-h-[92vh] flex flex-col overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.45)]"
              data-testid="course-modal"
            >
              <div className="overflow-y-auto flex-1 relative">
              <div className="sticky top-0 z-30 flex justify-end pointer-events-none">
                <button
                  onClick={onClose}
                  aria-label="Cerrar"
                  data-testid="course-modal-close"
                  className="pointer-events-auto m-2 md:m-3 w-10 h-10 flex items-center justify-center bg-white text-[#0a0a0a] border border-black/10 hover:bg-[#0a0a0a] hover:text-white hover:border-[#0a0a0a] transition-colors shadow-md"
                >
                  <X size={18} strokeWidth={1.6} />
                </button>
              </div>
              <div className="-mt-12 grid grid-cols-1 md:grid-cols-2">
                <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[560px] overflow-hidden bg-neutral-100">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-full object-cover"
                    data-testid="course-modal-image"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/65 to-transparent text-white">
                    <span className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold">
                      Programa Profesional
                    </span>
                  </div>
                </div>

                <div className="p-7 md:p-10 lg:p-12">
                  <span
                    className="text-[0.7rem] uppercase tracking-[0.22em] text-[#c40000] font-semibold"
                    data-testid="course-modal-tagline"
                  >
                    {course.tagline}
                  </span>
                  <h3
                    id="course-modal-title"
                    data-testid="course-modal-title"
                    className="mt-3 font-display font-semibold tracking-tighter text-3xl md:text-4xl text-[#0a0a0a] leading-tight"
                  >
                    {course.title}
                  </h3>

                  <p
                    className="mt-5 text-sm md:text-base font-light text-[#525252] leading-relaxed"
                    data-testid="course-modal-description"
                  >
                    {course.description}
                  </p>

                  <div className="mt-6 grid grid-cols-2 gap-4 border-t border-b border-black/8 py-5">
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[#525252] flex items-center gap-2">
                        <Clock size={12} strokeWidth={1.5} /> Duración
                      </div>
                      <div className="mt-1.5 text-sm font-semibold text-[#0a0a0a]">
                        {course.duration}
                      </div>
                    </div>
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[#525252] flex items-center gap-2">
                        <MapPin size={12} strokeWidth={1.5} /> Modalidad
                      </div>
                      <div className="mt-1.5 text-sm font-semibold text-[#0a0a0a]">
                        {course.modality}
                      </div>
                    </div>
                  </div>

                  {course.modules && course.modules.length > 0 && (
                    <div className="mt-6" data-testid="course-modal-modules">
                      <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#c40000] font-semibold">
                        Temario
                      </div>
                      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
                        {course.modules.map((m) => (
                          <li
                            key={m}
                            className="flex items-start gap-2 text-sm text-[#0a0a0a]"
                          >
                            <Check
                              size={14}
                              strokeWidth={2}
                              className="mt-1 shrink-0 text-[#c40000]"
                            />
                            <span>{m}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-6" data-testid="course-modal-includes">
                    <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#525252] font-semibold">
                      Lo que incluye
                    </div>
                    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
                      {(course.includes && course.includes.length > 0
                        ? course.includes
                        : defaultIncludes
                      ).map((it) => (
                        <li
                          key={it}
                          className="flex items-start gap-2 text-sm text-[#525252]"
                        >
                          <Check
                            size={14}
                            strokeWidth={2}
                            className="mt-1 shrink-0 text-[#0a0a0a]"
                          />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {course.schedules && course.schedules.length > 0 && (
                    <div className="mt-6" data-testid="course-modal-schedules">
                      <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#c40000] font-semibold flex items-center gap-2">
                        <CalendarClock size={12} strokeWidth={1.8} />
                        Turnos disponibles en horarios de
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {course.schedules.map((group, gi) => {
                          const isString = typeof group === "string";
                          const label = isString ? null : group.label;
                          const slots = isString ? [group] : group.slots || [];
                          return (
                            <div key={label || gi}>
                              {label && (
                                <div className="text-[0.65rem] uppercase tracking-[0.2em] text-[#525252] font-semibold mb-2">
                                  {label}
                                </div>
                              )}
                              <ul className="space-y-1.5">
                                {slots.map((s) => (
                                  <li
                                    key={s}
                                    className="text-sm text-[#0a0a0a] flex items-start gap-2"
                                  >
                                    <span className="mt-2 w-1 h-1 bg-[#c40000] rounded-full shrink-0" />
                                    <span>{s}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="mt-8 space-y-3">
                    {hasAnyPrice && (
                      <div
                        data-testid="course-modal-price-highlight"
                        className="bg-[#fafafa] border border-black/8 px-5 py-5 space-y-3 text-center"
                      >
                        <div>
                          {minPrice !== null ? (
                            <>
                              <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#525252] font-semibold">
                                Desde
                              </div>
                              <div className="mt-1 font-display font-bold text-3xl text-[#c40000] leading-none">
                                ${minPrice}{" "}
                                <span className="text-sm font-normal text-[#525252]">
                                  semanal
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#525252] font-semibold">
                                Inversión
                              </div>
                              <div className="mt-1 font-display font-bold text-xl text-[#c40000] leading-snug">
                                Consultar precio por sucursal
                              </div>
                            </>
                          )}
                        </div>
                        <div className="pt-3 border-t border-black/8 flex justify-center">
                          <button
                            type="button"
                            data-testid="course-modal-costs"
                            onClick={() => setCostsOpen(true)}
                            className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-[#0a0a0a] hover:text-[#c40000] transition-colors"
                          >
                            <Tag size={14} strokeWidth={1.6} />
                            Ver costos por sucursal
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        data-testid="course-modal-enroll"
                        onClick={() => setPickerMode("enroll")}
                        className="btn-primary w-full justify-center text-[0.7rem] px-4"
                      >
                        Inscribirme <ArrowRight size={14} strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        data-testid="course-modal-whatsapp"
                        onClick={() => setPickerMode("whatsapp")}
                        className="btn-whatsapp w-full justify-center text-[0.7rem] px-4"
                      >
                        <MessageCircle size={14} strokeWidth={1.8} /> WhatsApp
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BranchPickerModal
        open={!!pickerMode && !!course}
        mode={pickerMode}
        course={course}
        onClose={() => setPickerMode(null)}
        onSelect={handleBranchSelected}
      />

      <CostsModal
        open={costsOpen && !!course}
        course={course}
        onClose={() => setCostsOpen(false)}
        onPickBranch={() => setPickerMode("enroll")}
      />
    </>
  );
}
